import { merchantApi } from '@/config/axios'
import { getPortalArticlesByCategory, type PortalArticle } from '@/api/portal'

/** 方案分类 key：运营可自增，不再锁定四枚举 */
export type PortalSolutionCategoryKey = string

export interface PortalSolutionTabItem {
  key: PortalSolutionCategoryKey
  label: string
  listPage: boolean
  sort: number
  enabled: boolean
}

export interface PortalSolutionCatalogItem {
  id: string
  name: string
  shortName: string
  icon: string
  color: string
  gradient: string
  desc: string
  descLong: string
  products: string[]
  cases: number
  effect: string
  path: string
  featured: boolean
  categoryKey: PortalSolutionCategoryKey
  industry: string
  coverImage: string
}

export interface PortalProductCatalogItem {
  id: string
  name: string
  icon: string
  color: string
  bgColor: string
  desc: string
  descShort: string
  category: string
  features: string[]
  industries: string[]
  price: string
  path: string
}

/** 接口不可用时的分类兜底（正式展示以 tab/list 为准） */
const DEFAULT_SOLUTION_TABS: PortalSolutionTabItem[] = [
  { key: 'ecommerce', label: '电商解决方案', listPage: true, sort: 550, enabled: true },
  { key: 'cross-border', label: '跨境电商解决方案', listPage: true, sort: 540, enabled: true },
  { key: 'industry-suite', label: '行业整体解决方案', listPage: true, sort: 530, enabled: true },
  { key: 'smart-build', label: '智慧建设整体解决方案', listPage: true, sort: 520, enabled: true }
]

const publicHeaders = {
  isToken: false,
  // 访客页必须带租户；线上未传会 400「请求的租户标识未传递」
  'tenant-id': String(import.meta.env.VITE_APP_DEFAULT_TENANT_ID || 1)
}

/** icon 存 lucide 名或上传图 URL；URL/路径则按图片渲染 */
export function isPortalIconImage(value?: string) {
  const v = String(value || '').trim()
  if (!v) return false
  return (
    /^https?:\/\//i.test(v) ||
    v.startsWith('/') ||
    v.startsWith('data:image/') ||
    /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(v)
  )
}

function asCategoryKey(value: unknown): PortalSolutionCategoryKey {
  const key = String(value || '').trim()
  return key || 'ecommerce'
}

function stringArrayFromProducts(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String)
  if (typeof raw === 'string' && raw) {
    try {
      const arr = JSON.parse(raw)
      return Array.isArray(arr) ? arr.map(String) : []
    } catch {
      return []
    }
  }
  return []
}

interface AppTabRow {
  tabKey?: string
  name?: string
  listPage?: boolean
  sort?: number
}

interface AppEntryRow {
  tabKey?: string
  title?: string
  shortName?: string
  summary?: string
  effect?: string
  industry?: string
  slug?: string
  linkUrl?: string
  icon?: string
  color?: string
  coverImage?: string
  products?: string
  recommend?: boolean
}

function mapEntry(row: AppEntryRow): PortalSolutionCatalogItem {
  const id = String(row.slug || '')
  const color = String(row.color || '#0066FF')
  const categoryKey = asCategoryKey(row.tabKey)
  const shortName = String(row.shortName || row.title || '')
  const linkUrl = String(row.linkUrl || '').trim()
  return {
    id,
    name: String(row.title || ''),
    shortName,
    icon: String(row.icon || 'lucide:layers'),
    color,
    gradient: `linear-gradient(135deg,${color},#0891B2)`,
    desc: String(row.summary || ''),
    descLong: String(row.summary || ''),
    products: stringArrayFromProducts(row.products),
    cases: 0,
    effect: String(row.effect || ''),
    // 访客点击优先运营跳转链接；否则按 tab/slug 拼详情路径
    path:
      linkUrl ||
      (id
        ? categoryKey === 'industry'
          ? `/portal/solutions/${id}`
          : `/portal/solutions/${categoryKey}/${id}`
        : ''),
    featured: !!row.recommend,
    categoryKey,
    industry: String(row.industry || shortName),
    coverImage: String(row.coverImage || '')
  }
}

function parseExtra(article: PortalArticle) {
  try {
    return JSON.parse(article.extra || '{}') as Record<string, unknown>
  } catch {
    return {}
  }
}

function stringArray(value: unknown) {
  return Array.isArray(value) ? value.map(String) : []
}

export function mapPortalProduct(article: PortalArticle): PortalProductCatalogItem {
  const extra = parseExtra(article)
  const id = String(extra.slug || article.id)
  const targetProduct = String(extra.targetProduct || '').trim()
  const productId = targetProduct || id
  return {
    id,
    name: article.title,
    icon: String(extra.icon || 'lucide:box'),
    color: String(extra.color || '#0066FF'),
    bgColor: String(extra.bgColor || '#EBF5FF'),
    desc: article.summary || '',
    descShort: String(extra.descShort || article.summary || ''),
    category: String(extra.category || '企业服务'),
    features: stringArray(extra.features),
    industries: stringArray(extra.industries),
    price: String(extra.price || ''),
    // 跳转优先运营 link_url；否则落到真实产品 id（targetProduct）或导航 slug
    path: article.linkUrl || `/portal/products/${productId}`
  }
}

export async function getPortalProductCatalog() {
  const articles = await getPortalArticlesByCategory('portal-products')
  return articles.map(mapPortalProduct)
}

/** AI产品 Mega：走 admin-api 公开只读（线上仅反代 admin-api） */
export async function getPortalProductNavigation() {
  try {
    const rows = await merchantApi.get<PortalArticle[]>({
      url: '/admin-api/cms/portal-product-nav/list',
      headers: publicHeaders
    })
    return (rows || []).map(mapPortalProduct)
  } catch {
    return []
  }
}

export async function getPortalSolutionTabs(): Promise<PortalSolutionTabItem[]> {
  try {
    // 线上仅反代 admin-api；公开只读 @PermitAll（Security 已放行 tab/list、entry/list）
    const rows = await merchantApi.get<AppTabRow[]>({
      url: '/admin-api/portal-ops/portal-solution/tab/list',
      headers: publicHeaders
    })
    const tabs = (rows || [])
      .map((row) => ({
        key: asCategoryKey(row.tabKey),
        label: String(row.name || row.tabKey || ''),
        listPage: !!row.listPage,
        sort: Number(row.sort || 0),
        enabled: true
      }))
      .filter((t) => !!t.key)
      .sort((a, b) => b.sort - a.sort)
    return tabs.length ? tabs : DEFAULT_SOLUTION_TABS
  } catch {
    return DEFAULT_SOLUTION_TABS
  }
}

export async function getPortalSolutionCatalog() {
  try {
    const rows = await merchantApi.get<AppEntryRow[]>({
      url: '/admin-api/portal-ops/portal-solution/entry/list',
      headers: publicHeaders
    })
    return (rows || []).map(mapEntry)
  } catch {
    return []
  }
}

export async function getPortalSolutionCatalogByCategory(categoryKey?: PortalSolutionCategoryKey) {
  try {
    const rows = await merchantApi.get<AppEntryRow[]>({
      url: '/admin-api/portal-ops/portal-solution/entry/list',
      params: categoryKey ? { tabKey: categoryKey } : undefined,
      headers: publicHeaders
    })
    return (rows || []).map(mapEntry)
  } catch {
    return []
  }
}

export { DEFAULT_SOLUTION_TABS }
